/**
 * ParticipatoryDiagnosisCharts.js — Resolve os marcadores [GRAFICO_*] do Capítulo 3
 * (Diagnóstico Participativo) do PMAU, substituindo-os pelos gráficos Highcharts reais —
 * os mesmos exibidos na apresentação do workshop (SurveyDashboard.vue) — capturados via
 * `chart.getSVG()`, garantindo que o gráfico do capítulo seja idêntico ao da apresentação.
 *
 * Os nomes dos marcadores abaixo (DIAGNOSIS_CHART_MARKERS) espelham
 * `_DIAGNOSIS_CHART_CODES`/`_DIAGNOSIS_PROFILE_CODES` em
 * ai-service/app/generation/service.py — os dois lados precisam ficar em sincronia.
 */

import Highcharts from "highcharts";
import HighchartsExporting from "highcharts/modules/exporting";
import SurveyService from "../servico/SurveyService";
import {
  buildChartOptions,
  buildProfileDonut,
  buildProfileBar,
  CHART_TYPE_BY_CODE,
  PROFILE_QUESTION_CONFIGS,
} from "./SurveyCharts";

// chart.getSVG() só existe depois que o módulo Exporting é registrado — o core do
// Highcharts (importado em main.js) não inclui esse método por padrão.
if (typeof HighchartsExporting === "function") {
  HighchartsExporting(Highcharts);
}

const DIAGNOSIS_CHART_MARKERS = {
  id_bairro: "[GRAFICO_BAIRRO]",
  eval_classification: "[GRAFICO_CLASSIFICACAO]",
  eval_beauty_scale: "[GRAFICO_AVALIACAO_BELEZA]",
  eval_mental_health: "[GRAFICO_SAUDE_MENTAL]",
  eval_problems: "[GRAFICO_PROBLEMAS]",
  eval_maintenance: "[GRAFICO_MANUTENCAO]",
  eval_investment_likert: "[GRAFICO_INVESTIMENTO]",
  eval_urban_trees: "[GRAFICO_ARVORES_URBANAS]",
  eval_wishes: "[GRAFICO_DESEJOS]",
  eval_more_trees: "[GRAFICO_MAIS_ARVORES]",
};

const CUSTOM_CHART_TYPES = ["scale", "single_choice", "multiple_choice"];
const HAS_MARKER_RE = /\[GRAFICO_[A-Z0-9_]+\]/;
const MARKER_RE = /\[GRAFICO_[A-Z0-9_]+\]/g;

/**
 * chart.getSVG() sempre grava width/height em pixels no <svg> raiz, mas nunca um
 * viewBox. Sem viewBox, redimensionar via CSS (max-width:100%) não reescala o
 * desenho — apenas encolhe a "janela" visível e corta o conteúdo que ultrapassa a
 * nova largura. Injetamos o viewBox aqui para que o gráfico possa ser exibido
 * responsivamente (Quill) sem esconder parte do conteúdo.
 */
function withViewBox(svg) {
  if (/\sviewBox=/.test(svg)) return svg;
  const wMatch = svg.match(/<svg[^>]*\swidth="(\d+(?:\.\d+)?)"/);
  const hMatch = svg.match(/<svg[^>]*\sheight="(\d+(?:\.\d+)?)"/);
  if (!wMatch || !hMatch) return svg;
  return svg.replace(/<svg\s/, `<svg viewBox="0 0 ${wMatch[1]} ${hMatch[1]}" `);
}

function renderSvg(options, width, height) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.width = width + "px";
  container.style.height = height + "px";
  document.body.appendChild(container);
  try {
    const chart = Highcharts.chart(container, options);
    const svg = withViewBox(chart.getSVG());
    chart.destroy();
    return svg;
  } finally {
    document.body.removeChild(container);
  }
}

function figureHtml(svg, caption) {
  return "<figure>" + svg + (caption ? "<figcaption>" + caption + "</figcaption>" : "") + "</figure>";
}

function replaceAll(html, marker, replacement) {
  return html.split(marker).join(replacement);
}

/**
 * Substitui os marcadores [GRAFICO_*] presentes em `html` pelos gráficos reais da conta,
 * consolidando os dados atuais do questionário. Nunca lança exceção — em caso de falha,
 * remove os marcadores não resolvidos para não vazar texto de marcador no capítulo salvo.
 * Cada marcador é resolvido isoladamente: uma falha ao renderizar um gráfico não afeta
 * os demais. Erros são logados no console para diagnóstico.
 *
 * Retorna { html, failedMarkers } — failedMarkers lista os marcadores que não puderam
 * ser resolvidos (sem dados, gráfico não reconhecido, ou erro de renderização/rede),
 * para o chamador poder avisar o usuário em vez de salvar espaços em branco silenciosamente.
 */
export async function resolveDiagnosisCharts(html, contaId) {
  if (!html || !HAS_MARKER_RE.test(html)) return { html, failedMarkers: [] };

  let result = html;
  const failedMarkers = [];

  function resolveMarker(marker, question, chartType, width) {
    if (!result.includes(marker)) return;
    if (!question || !question.total_answers) {
      result = replaceAll(result, marker, "");
      failedMarkers.push(marker);
      return;
    }
    try {
      const options = buildChartOptions(chartType, question, 420);
      const height = (options.chart && options.chart.height) || 420;
      const svg = renderSvg(options, width, height);
      result = replaceAll(result, marker, figureHtml(svg, question.question_text));
    } catch (e) {
      console.error("[ParticipatoryDiagnosisCharts] Falha ao renderizar " + marker, e);
      result = replaceAll(result, marker, "");
      failedMarkers.push(marker);
    }
  }

  try {
    const formResp = await SurveyService.GetForm(contaId);
    const formId = formResp.data.id;
    const [baseResp, reportResp] = await Promise.all([
      SurveyService.ListBaseQuestions(),
      SurveyService.GetReport(formId),
    ]);

    const codeById = {};
    baseResp.data.forEach((q) => { codeById[q.id] = q.code; });

    const questions = (reportResp.data.questions || []).map((q) => ({
      ...q,
      code: codeById[q.question_id] || null,
    }));
    const byCode = {};
    questions.forEach((q) => { if (q.code) byCode[q.code] = q; });

    // ── [GRAFICO_PERFIL] — 3 figuras: gênero, faixa etária, escolaridade ──────────
    if (result.includes("[GRAFICO_PERFIL]")) {
      try {
        const profileQs = PROFILE_QUESTION_CONFIGS
          .map((c) => (byCode[c.code] && byCode[c.code].total_answers > 0
            ? { ...byCode[c.code], chartStyle: c.chartStyle }
            : null))
          .filter(Boolean);

        if (profileQs.length === 0) {
          result = replaceAll(result, "[GRAFICO_PERFIL]", "");
          failedMarkers.push("[GRAFICO_PERFIL]");
        } else {
          let profileHtml = "";
          profileQs.forEach((q) => {
            const options = q.chartStyle === "donut" ? buildProfileDonut(q) : buildProfileBar(q);
            const width = q.chartStyle === "donut" ? 420 : 640;
            const height = (options.chart && options.chart.height) || 320;
            profileHtml += figureHtml(renderSvg(options, width, height), q.question_text);
          });
          result = replaceAll(result, "[GRAFICO_PERFIL]", profileHtml);
        }
      } catch (e) {
        console.error("[ParticipatoryDiagnosisCharts] Falha ao renderizar [GRAFICO_PERFIL]", e);
        result = replaceAll(result, "[GRAFICO_PERFIL]", "");
        failedMarkers.push("[GRAFICO_PERFIL]");
      }
    }

    // ── Marcadores fixos (avaliação + bairro) ─────────────────────────────────────
    Object.keys(DIAGNOSIS_CHART_MARKERS).forEach((code) => {
      const marker = DIAGNOSIS_CHART_MARKERS[code];
      const chartType = CHART_TYPE_BY_CODE[code];
      const width = chartType === "donut" || chartType === "gauge" ? 560 : 900;
      resolveMarker(marker, byCode[code], chartType, width);
    });

    // ── Marcadores dinâmicos [GRAFICO_CUSTOM_N] — perguntas customizadas da conta ──
    const customQs = questions.filter(
      (q) => !q.code && !q.is_archived && CUSTOM_CHART_TYPES.includes(q.question_type)
    );
    customQs.forEach((q, idx) => {
      const marker = "[GRAFICO_CUSTOM_" + (idx + 1) + "]";
      const chartType = q.question_type === "scale" ? "gauge" : "bar_h";
      resolveMarker(marker, q, chartType, chartType === "gauge" ? 560 : 900);
    });
  } catch (e) {
    console.error("[ParticipatoryDiagnosisCharts] Falha ao consolidar dados do questionário", e);
  }

  // Remove qualquer marcador remanescente (sem dados, ou falha ao consolidar) e registra.
  const remaining = result.match(MARKER_RE) || [];
  remaining.forEach((m) => failedMarkers.push(m));
  result = result.replace(MARKER_RE, "");

  if (failedMarkers.length > 0) {
    console.warn("[ParticipatoryDiagnosisCharts] Marcadores não resolvidos:", failedMarkers);
  }

  return { html: result, failedMarkers };
}
